'use client'

import { Button } from '@/core/components/form/button/button'
import { useDeferredValue, useState } from 'react'
import { LuCheck, LuPlus, LuTrash2 } from 'react-icons/lu'

import { Checkbox } from '@/core/components/form/checkbox'
import { InputText } from '@/core/components/form/input-text'
import TagInput from '@/core/components/form/tag-input'
import { cn } from '@/core/utils/common'

import CustomScreen from '@/features/veluvit/components/chat/custom-screen'
import { renderScreen, stepNumbers } from '@/features/veluvit/lib/custom-screen'
import {
  Btn,
  Card,
  Field,
  Fold,
  Select,
  StepHead,
  TextArea,
  TextField,
} from '@/features/studio-renew/components/ui'
import { STUDIO_TEXT_LIMITS } from '@/features/studio-renew/lib/studio-text-limits'
import {
  STATUS_ACCENTS,
  STATUS_THEMES,
} from '@/features/studio-renew/status-themes'
import { newRowId, useRenewStore } from '@/features/studio-renew/store'

import type { StatusBoard, StatusField } from '@/features/studio-renew/types'

/* 상태창 — 작가가 항목을 정하고 HTML 로 화면을 짠다. 모델은 매 턴 이 항목 key 로 값을 돌려준다(tresponse, 기획 §7).
   호감도·관계 같은 고정 항목은 없다. 자주 쓰는 묶음은 프리셋으로 불러와 고친다(AI 아님).
   독립 재료라 여러 작품이 같은 상태창을 연결해 쓴다 — 여기서 고치면 연결한 작품 전부에 반영된다. */

const MAX_FIELDS = 12
/** 선택형 선택지 수 */
const MAX_OPTIONS = 8
const update = (board: StatusBoard, patch: Partial<StatusBoard>) =>
  useRenewStore.getState().updateStatus(board.id, patch)

const field = (
  key: string,
  type: StatusField['type'],
  extra: Partial<StatusField> = {}
): StatusField => ({
  id: newRowId(),
  key,
  type,
  initial: '',
  options: [],
  rule: '',
  ...extra,
})
const PRESETS: { name: string; fields: () => StatusField[] }[] = [
  {
    name: '장면',
    fields: () => [
      field('날짜', 'text'),
      field('시간', 'text'),
      field('장소', 'text'),
      field('표정', 'text'),
      field('속마음', 'text', { rule: '방금 한 말 뒤에 숨긴 생각 한 줄' }),
    ],
  },
  {
    name: '관계',
    fields: () => [
      field('호감도', 'number', {
        initial: '0',
        min: 0,
        max: 100,
        rule: '다정한 말에 +1~3, 무례하면 -5',
      }),
      field('관계', 'list', {
        options: ['낯선 사이', '친구', '썸', '연인'],
        ordered: true,
        initial: '낯선 사이',
        rule: '서로 마음을 확인하면 다음 단계로, 크게 다투면 한 단계 내려가요',
      }),
    ],
  },
]

// 기본 틀(한 줄씩 이름 · 값)은 디자인 템플릿(status-themes.ts)으로 바뀌었다

/** 미리보기 값 — 실제 대화 시작값과 같은 값. 두 변수 표기 모두 허용하고, 선택형은 {{항목.단계}}(몇 번째)도 채운다(앱 boardValues 와 같은 규칙) */
export const initialValues = (fields: StatusField[]) => {
  const visible = fields.filter((field) => !field.hidden)
  const base = Object.fromEntries(visible.map((f) => [f.key, f.initial]))
  return {
    ...Object.fromEntries(visible.map((f) => [`status.${f.key}`, f.initial])),
    ...base,
    ...stepNumbers(visible, base),
  }
}

export default function StatusStep({ board }: { board: StatusBoard }) {
  const { fields, html } = board
  // 서버는 저장하며 HTML 을 인라인 div 로 다시 쓴다. 직접 안 고친 디자인은 대화방처럼 템플릿 코드로 그린다
  const theme =
    board.design && !board.design.edited
      ? STATUS_THEMES.find((t) => t.id === board.design?.theme)
      : undefined
  const screen =
    theme && board.design ? theme.render(fields, board.design.accent) : html
  // 고른 디자인 그대로면 항목이 바뀔 때 디자인도 다시 그린다 — 템플릿이 항목 수 · 종류에 맞춰 늘고 준다
  const setFields = (next: StatusField[]) => {
    update(
      board,
      theme && board.design
        ? { fields: next, html: theme.render(next, board.design.accent) }
        : { fields: next }
    )
  }
  const patch = (id: string, p: Partial<StatusField>) =>
    setFields(fields.map((f) => (f.id === id ? { ...f, ...p } : f)))
  const addPreset = (make: () => StatusField[]) => {
    const have = new Set(fields.map((f) => f.key))
    setFields(
      [...fields, ...make().filter((f) => !have.has(f.key))].slice(
        0,
        MAX_FIELDS
      )
    )
  }
  // 미리보기는 입력보다 한 박자 늦게 — 타이핑 중 iframe 을 매번 다시 그리지 않게
  const preview = useDeferredValue(
    screen ? renderScreen(screen, initialValues(fields)) : ''
  )
  const showDesign = fields.length > 0 || !!html.trim() || !!board.design

  return (
    <>
      <StepHead title="상태창" />
      <Card>
        <Field
          label="상태창 이름"
          required
          invalid={!board.name.trim()}
          hint="이용자에게 비공개"
        >
          <TextField
            value={board.name}
            onChange={(name) => update(board, { name })}
            max={STUDIO_TEXT_LIMITS.name}
            placeholder="예) 호감도 · 장소 상태창"
          />
        </Field>
      </Card>
      <Card>
        <Field
          label="표시할 정보"
          required
          invalid={!fields.length}
          right={
            <span className="text-c12 whitespace-nowrap text-description tabular-nums over10:text-c14">
              현재 {fields.length}개 · 최대 {MAX_FIELDS}개
            </span>
          }
        >
          <div className="mb-5 flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <Btn
                key={p.name}
                size="sm"
                disabled={fields.length >= MAX_FIELDS}
                onClick={() => addPreset(p.fields)}
              >
                <LuPlus aria-hidden="true" className="size-3.5" />
                {p.name} 정보 추가
              </Btn>
            ))}
          </div>
          <div className="grid gap-2.5">
            {fields.map((f) => (
              <div
                key={f.id}
                className="border-t border-border pt-5 first:border-t-0 first:pt-0"
              >
                <div className="grid gap-2 over6:grid-cols-[1fr_120px_1fr_auto]">
                  <TextField
                    value={f.key}
                    onChange={(v) =>
                      patch(f.id, { key: v.replace(/[{}#/]/g, '') })
                    }
                    max={STUDIO_TEXT_LIMITS.statusKey}
                    placeholder="항목 이름"
                  />
                  <Select
                    label="항목 종류"
                    className="w-full"
                    value={f.type}
                    onChange={(type) => patch(f.id, { type })}
                    options={[
                      { v: 'text' as const, label: '글' },
                      { v: 'number' as const, label: '숫자' },
                      { v: 'list' as const, label: '선택형' },
                    ]}
                  />
                  {/* 선택형의 시작값은 선택지 중에서 고른다(아무 글이나 넣으면 모델이 목록 밖 값을 받는다) */}
                  {f.type === 'list' ? (
                    <Select
                      label="시작값"
                      className="w-full"
                      value={f.options.includes(f.initial) ? f.initial : ''}
                      onChange={(initial) => patch(f.id, { initial })}
                      options={[
                        {
                          v: '',
                          label: f.options.length
                            ? '시작값 고르기'
                            : '선택지를 먼저 적어 주세요',
                        },
                        ...f.options.map((o) => ({ v: o, label: o })),
                      ]}
                    />
                  ) : (
                    <TextField
                      value={f.initial}
                      onChange={(v) => patch(f.id, { initial: v })}
                      max={STUDIO_TEXT_LIMITS.statusInitial}
                      placeholder="시작값"
                    />
                  )}
                  <Btn
                    tone="danger"
                    size="icon"
                    aria-label={`${f.key || '항목'} 삭제`}
                    onClick={() =>
                      setFields(fields.filter((x) => x.id !== f.id))
                    }
                  >
                    <LuTrash2 aria-hidden="true" className="size-3.5" />
                  </Btn>
                </div>
                {f.type === 'number' && (
                  <div className="mt-2 flex items-center gap-2 text-c12 over10:text-c14 text-description">
                    범위
                    <InputText
                      type="number"
                      value={f.min ?? ''}
                      onChange={(e) =>
                        patch(f.id, {
                          min:
                            e.target.value === ''
                              ? undefined
                              : Number(e.target.value),
                        })
                      }
                      aria-label="최소"
                      classNames={{ container: 'w-24' }}
                    />
                    ~
                    <InputText
                      type="number"
                      value={f.max ?? ''}
                      onChange={(e) =>
                        patch(f.id, {
                          max:
                            e.target.value === ''
                              ? undefined
                              : Number(e.target.value),
                        })
                      }
                      aria-label="최대"
                      classNames={{ container: 'w-24' }}
                    />
                  </div>
                )}
                {/* 선택형 — 값은 칩으로 하나씩(정확히 같은 글자로 돌려받아야 해서 자유 글로 받지 않는다), 바뀌는 조건은 아래 변화 규칙에 자연어로 */}
                {f.type === 'list' && (
                  <div className="mt-2">
                    <TagInput
                      value={f.options}
                      onChange={(options) =>
                        patch(f.id, {
                          options,
                          initial: options.includes(f.initial) ? f.initial : '',
                        })
                      }
                      max={MAX_OPTIONS}
                      maxLength={STUDIO_TEXT_LIMITS.statusOption}
                      ordered={!!f.ordered}
                      ariaLabel={`${f.key || '항목'} 선택지`}
                      placeholder="선택지를 적고 Enter · 예) 낯선 사이"
                    />
                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                      <Checkbox
                        checked={!!f.ordered}
                        onChange={(e) =>
                          patch(f.id, { ordered: e.target.checked })
                        }
                        classNames={{
                          container: 'flex-none',
                          label: 'text-c14 text-foreground',
                        }}
                        label="단계형"
                      />
                      <span className="min-w-0 text-c13 text-description over10:text-c14">
                        {f.ordered && f.options.length > 1
                          ? `${f.options.join(' › ')} — 한 번에 한 칸씩만 움직여요`
                          : f.ordered
                            ? '칩을 끌어 순서를 바꿔요'
                            : f.options.length
                              ? `캐릭터는 이 ${f.options.length}개 중 하나로만 바꿔요`
                              : '선택지를 2개 이상 넣어 주세요'}
                      </span>
                    </div>
                  </div>
                )}
                <div className="mt-2 flex items-center gap-2">
                  <TextField
                    className="flex-1"
                    value={f.rule}
                    onChange={(v) => patch(f.id, { rule: v })}
                    max={STUDIO_TEXT_LIMITS.statusRule}
                    placeholder="변화 규칙(선택사항) · 예) 다정한 말에 +1~3"
                  />
                  <Checkbox
                    checked={!!f.hidden}
                    onChange={(e) => patch(f.id, { hidden: e.target.checked })}
                    classNames={{
                      container: 'flex-none',
                      label: 'text-c14 text-description',
                    }}
                    label="숨김"
                  />
                </div>
              </div>
            ))}
          </div>
          <Btn
            className="mt-3"
            disabled={fields.length >= MAX_FIELDS}
            onClick={() => setFields([...fields, field('', 'text')])}
          >
            <LuPlus aria-hidden="true" className="size-4" />
            항목 추가
          </Btn>
          {!!fields.length && (
            <p className="mt-3 text-c13 leading-6 text-description over10:text-c14">
              ‘숨김’ 항목은 이용자에게 미표시
            </p>
          )}
        </Field>
      </Card>
      {showDesign && (
        <>
          <Card>
            <DesignPicker board={board} />
            {preview && (
              <Field label="미리보기">
                {/* 대화방과 같은 배경에서 저장된 디자인을 확인한다. */}
                <div className="rounded-panel bg-[radial-gradient(120%_90%_at_85%_0%,#2b2233,#0e0d12_72%)] p-4">
                  <CustomScreen title="상태창 미리보기" html={preview} />
                </div>
              </Field>
            )}
          </Card>
          <Fold
            title="화면 직접 꾸미기"
            open={!!board.design?.edited || (!!html.trim() && !board.design)}
          >
            <Field
              label="화면 HTML"
              hint={
                <>
                  항목 이름을 {'{{항목}}'} 또는 {'{{status.항목}}'} 으로 넣어요.
                  스크립트 · 링크 · 외부 이미지는 지워져요. 직접 고친 뒤로는
                  항목을 바꿔도 디자인이 다시 그려지지 않아요.
                </>
              }
            >
              {/* 직접 고치면 design.edited — 앱은 고친 HTML 을 그대로 그리고, 안 고친 템플릿은 템플릿 코드로 다시 그린다(서버 정리로 꾸밈이 빠지지 않게) */}
              <TextArea
                value={screen}
                onChange={(v) =>
                  update(board, {
                    html: v,
                    ...(board.design
                      ? { design: { ...board.design, edited: true } }
                      : {}),
                  })
                }
                rows={12}
                mono
              />
            </Field>
          </Fold>
        </>
      )}
    </>
  )
}

/**
 * 디자인 고르기 — 템플릿 5개를 지금 항목 · 예시 값으로 그려 보여 주고, 포인트 색을 고른다.
 * 직접 고친 HTML 을 덮어쓸 때는 바로 되돌리기를 둔다(확인 창 대신).
 */
function DesignPicker({ board }: { board: StatusBoard }) {
  const { fields, html, design } = board
  const [undo, setUndo] = useState<string | null>(null)
  const accent =
    STATUS_ACCENTS.find((a) => a.color === design?.accent) ?? STATUS_ACCENTS[0]
  const values = initialValues(fields)
  // 서버가 저장하며 HTML 을 다시 쓰므로 글자 비교로 판단하면 저장 뒤 선택이 풀린다
  const edited = !!html.trim() && (!design || !!design.edited)
  const pick = (theme: string, color: string) => {
    const t = STATUS_THEMES.find((x) => x.id === theme)
    if (!t) return
    setUndo(edited ? html : null)
    update(board, {
      html: t.render(fields, color),
      design: { theme, accent: color },
    })
  }

  return (
    <Field
      label="디자인"
      hint={edited ? '선택 시 직접 수정한 디자인을 덮어씀' : undefined}
      right={
        <div
          role="radiogroup"
          aria-label="포인트 색"
          className="flex items-center gap-1.5"
        >
          {STATUS_ACCENTS.map((a) => (
            <Btn
              key={a.id}
              tone="ghost"
              size="icon-sm"
              role="radio"
              aria-checked={accent.id === a.id}
              aria-label={a.label}
              // 예전 디자인(없어진 템플릿 id)을 쓰던 상태창도 색을 누르면 첫 템플릿으로 그려진다
              onClick={() =>
                pick(design?.theme ?? STATUS_THEMES[0].id, a.color)
              }
              disabled={!fields.length}
              className="size-7! min-h-0! rounded-full! p-0! hover:bg-transparent!"
            >
              <span
                aria-hidden="true"
                className={cn(
                  'block size-6 rounded-full transition-[box-shadow] duration-150',
                  accent.id === a.id &&
                    'shadow-[0_0_0_2px_var(--color-background),0_0_0_4px_var(--color-foreground)]'
                )}
                style={{ background: a.color }}
              />
            </Btn>
          ))}
        </div>
      }
    >
      {undo != null && (
        <div
          role="status"
          className="mb-3 flex items-center gap-3 text-c14 text-gray over10:text-c15"
        >
          직접 고친 디자인을 바꿨어요.
          <Btn
            tone="ghost"
            size="sm"
            className="text-point!"
            onClick={() => {
              update(board, { html: undo, design: undefined })
              setUndo(null)
            }}
          >
            되돌리기
          </Btn>
        </div>
      )}
      {!fields.length ? (
        <p className="text-c14 text-description over10:text-c15">
          항목 1개 이상 필요
        </p>
      ) : (
        <div className="grid gap-3 over6:grid-cols-2">
          {STATUS_THEMES.map((t) => {
            const on = !edited && design?.theme === t.id
            return (
              <Button
                key={t.id}
                type="button"
                aria-pressed={on}
                onClick={() => pick(t.id, accent.color)}
                className={cn(
                  'min-w-0 cursor-pointer overflow-hidden rounded-panel bg-surface text-left transition-shadow duration-150',
                  on
                    ? 'shadow-[0_0_0_2px_var(--color-foreground)]'
                    : 'shadow-[0_0_0_1px_color-mix(in_srgb,var(--color-foreground)_10%,transparent)] hover:shadow-[0_0_0_1px_color-mix(in_srgb,var(--color-foreground)_32%,transparent)]'
                )}
                variant="bare"
              >
                {/* 미리보기 — 대화방처럼 어두운 장면 위에 얹어 실제로 뜨는 모습으로 본다. 눌러지지 않게(누름은 칸 전체가 받는다) */}
                <span className="pointer-events-none block max-h-64 overflow-hidden bg-[radial-gradient(120%_90%_at_85%_0%,#2b2233,#0e0d12_72%)] px-3 pt-4 pb-3">
                  <CustomScreen
                    title={`${t.name} 미리보기`}
                    html={renderScreen(t.render(fields, accent.color), values)}
                  />
                </span>
                <span className="flex items-center gap-1.5 px-3.5 pt-3">
                  <span className="text-c15 font-semibold text-foreground over10:text-c16">
                    {t.name}
                  </span>
                  {on && (
                    <LuCheck
                      aria-hidden="true"
                      className="size-4 text-foreground"
                      strokeWidth={3}
                    />
                  )}
                </span>
                <span className="block px-3.5 pt-0.5 pb-3.5 text-c13 text-description over10:text-c14">
                  {t.desc}
                </span>
              </Button>
            )
          })}
        </div>
      )}
    </Field>
  )
}
